import {OtsThemeRoot,CompliancePdfPreview} from '@ots-frontend/ots-ui-tw';
import {Card, Container, Group, Menu, rem, Space, Tabs, Text, Title, UnstyledButton} from "@mantine/core";
import {useLocation, useNavigate} from "react-router-dom";
import {PropsWithChildren, useCallback} from "react";
import {RrcEp10WasteManifestEntity} from "@ots-frontend/ots-data";
import {RrcEp10WasteManifestProfileDetails} from "./RrcEp10WasteManifestProfileDetails";
import {createContentStyles} from "../../../theme/useContentStyles";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {
  RrcEp10WasteManifestAction,
  RrcEp10WasteManifestActionMenuDropdown
} from "../../menu/RrcEp10WasteManifestActionMenu/RrcEp10WasteManifestActionMenu";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {TabItem} from "../../tabs/Tabs/Tabs";
import {Badge} from "../../badge/Badge/Badge";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {useDownloadRrcEp10WasteManifest} from "../../../hooks/useDownloadRrcEp10WasteManifest";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";

const useStyles = createContentStyles((theme) => ({
  mainSection: {
    paddingBottom: theme.spacing.sm
  }
}));

export type RrcEp10WasteManifestProfileProps = PropsWithChildren<{
  rrcEp10WasteManifest: RrcEp10WasteManifestEntity
}>

const tabs: TabItem[] = [
  {
    key: "details",
    value: "Details"
  },
];

export const RrcEp10WasteManifestProfile = (props: RrcEp10WasteManifestProfileProps) => {
  const {rrcEp10WasteManifest} = props;

  const {classes} = useStyles();

  const {t} = useTranslation()
  const {baseQueryApi} = useApp()
  const navigate = useNavigate();
  const {pathname, search, state} = useLocation();

  const {
    downloadRrcEp10WasteManifest,
  } = useDownloadRrcEp10WasteManifest({baseQueryApi})

  const onActionClick = useCallback((action: RrcEp10WasteManifestAction) => {
    if (action === "DOWNLOAD") {
      downloadRrcEp10WasteManifest(rrcEp10WasteManifest.id, rrcEp10WasteManifest.tenantCode)
    }
  }, [rrcEp10WasteManifest, downloadRrcEp10WasteManifest])

  const items = tabs.map((tab) => (
    <HasAccess accessOnEmpty={true} permissions={tab.permissions} key={tab.key}>
      <Tabs.Tab value={tab.key}>
        {tab.value}
      </Tabs.Tab>
    </HasAccess>
  ));

  return (
    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
      <Container fluid={true} className={classes.mainSection}>
        <Group position="apart">
          <Group spacing="xs">
            <Title order={4}>RRC EP-10 Waste Manifest: {rrcEp10WasteManifest.manifestNumber}</Title>
          </Group>

          <Menu
            width={260}
            position="bottom-end"
            transitionProps={{transition: "pop-top-right"}}
            withinPortal
          >
            <Menu.Target>
              <UnstyledButton>
                <Group spacing={7}>
                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                    {t("common.action.plural", "Actions")}
                  </Text>
                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <RrcEp10WasteManifestActionMenuDropdown
                rrcEp10WasteManifest={rrcEp10WasteManifest}
                onActionClick={onActionClick}/>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Container>

      <Card.Section>
        <Container fluid={true}>
          <Tabs
            defaultValue={state?.tabValue ?? "details"}
            onTabChange={(value) => navigate(pathname + search, {
              replace: true,
              state: {...state, tabValue: value}
            })}
          >
            <Tabs.List>{items}</Tabs.List>
            <Space h="md"/>
            <Tabs.Panel value="details">
              <RrcEp10WasteManifestProfileDetails rrcEp10WasteManifest={rrcEp10WasteManifest}/>
              <OtsThemeRoot className="mt-4"><CompliancePdfPreview document={{program:'ep10',id:rrcEp10WasteManifest.id,tenantCode:rrcEp10WasteManifest.tenantCode}}
                label={`EP-10 PDF · ${rrcEp10WasteManifest.manifestNumber} · Version ${rrcEp10WasteManifest.version}`} /></OtsThemeRoot>
            </Tabs.Panel>
          </Tabs>
        </Container>
      </Card.Section>
    </Card>
  );
};
